// Imports
import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components/macro';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Textarea from '../../../../../../../shareables/foundation/front-end/components/forms/fields/textarea';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetProfileResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/profile/get';
import {
	PutProfileFiles,
	PutProfileParameters,
} from '../../../../../../../shareables/types/api/main/website/member/settings/profile/put';
import UploadProfilePhoto from '../../shared-components/upload-profile-photo';
import SocialURLs from './social-urls';
import { useOutletContext } from 'react-router-dom';
import removePersistedFormEntry from '../../../../../../../utils/remove-persisted-form-entry';


// Styled components
const Container = styled.div`
	line-height: 1.3125rem;
`;

const AdditionalInformationNote = styled.p`
	margin-bottom: 1.25rem;
`;


// Define the accepted props
interface Props {
	storybookProfileSettings?: GetProfileResponse;
	storybookRemovePhoto?: true;
}


// Function component
const Profile: React.FC<Props> = ({ storybookProfileSettings, storybookRemovePhoto }) => {
	// Use state
	const [profileSettings, setProfileSettings] = useState<GetProfileResponse | null>(storybookProfileSettings ?? null);
	const [removePhoto, setRemovePhoto] = useState(storybookRemovePhoto ?? false);
	const [newProfilePhoto, setNewProfilePhoto] = useState<File>();
	
	
	// Use ref
	const profilePictureSetter = useRef<(file: File | null) => void>();
	const resetCanvasSource = useRef<() => void>();
	const fileRef = useRef<HTMLInputElement>(null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const user = useAppSelector((state) => state.common.user);
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (!(storybookProfileSettings || storybookRemovePhoto) && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, profileSettings, storybookProfileSettings, storybookRemovePhoto]);
	
	
	// Function that loads profile settings
	const memoizedLoadProfileSettings = useCallback(
		(onComplete?: (profilePhotoURL: GetProfileResponse['profilePhotoURL']) => void) => {
			void dispatch(
				apiActions.call<undefined, undefined, GetProfileResponse>({
					action: 'GET',
					uri: `/website/${window.location.hostname}/member/settings/profile`,
					completion: (json) => {
						setProfileSettings(json.response);
						onComplete?.(json.response.profilePhotoURL);
					},
				})
			);
		},
		[dispatch]
	);
	
	
	// Load profile settings on mount
	useEffect(() => {
		memoizedLoadProfileSettings();
	}, [memoizedLoadProfileSettings]);
	
	
	// If loading, then display nothing
	if (profileSettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutProfileParameters, PutProfileFiles, undefined> = {
		action: 'PUT',
		uri: `/website/${window.location.hostname}/member/settings/profile`,
		submitButton: {
			text: 'Save',
		},
		extraParameters: {
			removeProfilePhoto: removePhoto,
		},
		extraFiles: {
			profilePhoto: removePhoto ? undefined : newProfilePhoto,
		},
		recoverState: false,
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Display success message
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'These settings have been saved.',
			})
		);
		
		
		// Reset image form field
		profilePictureSetter.current?.(null);
		resetCanvasSource.current?.();
		
		if (fileRef.current?.value) {
			fileRef.current.value = '';
		}
		
		setRemovePhoto(false);
		
		
		// Reset form
		removePersistedFormEntry(`PUT /website/${window.location.hostname}/member/settings/profile`);
		
		
		// Reload profile settings and sync updated photo URL to Redux
		//  -> Triggers the profile photo in the top bar to update
		memoizedLoadProfileSettings((profilePhotoURL) => {
			dispatch(commonActions.signedIn({ ...user, profilePhotoURL }));
		});
	};
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			<AdditionalInformationNote>
				<em>
					This additional information will show up in the resident directory (if available) to help other members get to
					know you better.
				</em>
			</AdditionalInformationNote>
			
			<APIForm options={formOptions} onCompletion={handleFormCompletion}>
				<UploadProfilePhoto
					setNewProfilePhoto={setNewProfilePhoto}
					setRemovePhoto={setRemovePhoto}
					removePhoto={removePhoto}
					profilePictureSetter={profilePictureSetter}
					resetCanvasSource={resetCanvasSource}
					fileRef={fileRef}
					profilePhotoURL={profileSettings.profilePhotoURL}
					defaultAvatarSize='FULL'
				/>
				
				<Input
					name='employer'
					label='Employer'
					placeholder='Acme, Inc.'
					type='text'
					initialValue={profileSettings.employer ?? undefined}
					maxLength={50}
				/>
				
				<Textarea
					name='about'
					label='About me'
					placeholder='A short description about you.'
					rows={3}
					initialValue={profileSettings.about ?? undefined}
					maxLength={200}
					displayCharacterCount
				/>
				
				<SocialURLs socialURLs={profileSettings.socialURLs} />
			</APIForm>
		</Container>
	);
};

export default Profile;
